import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ICareMap } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { WithLinks } from '../caremap/WithLinks';
import { AlertPresenter } from '../AlertPresenter';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { Logger } from '../support/Logger';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { InsightsModel } from './InsightsModel';

export class CaremapInsightsModelFactory extends State<Record<string, never>> {
    static inject = () => [
        CareMapState,
        APIClient,
        AlertPresenter,
        BufferedEventBus,
        Logger,
        MixPanelEventEmitter
    ];
    constructor(
        public careMapState: CareMapState,
        public apiClient: APIClient,
        public alert: AlertPresenter,
        public events: BufferedEventBus,
        public logger: Logger,
        public tracking: MixPanelEventEmitter
    ) {
        super({});

        // events allows a limited number of subscribers
        // so subscribe once here and pass the message on
        this.events.on('web-sockets.message', msg => {
            for (const model of Object.values(this.careMapIdToModel)) {
                model.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
        });
    }

    private activeCareMapId = '';
    private careMapIdToModel: { [careMapId: string]: InsightsModel } = {};

    forActiveCaremap = (): InsightsModel => {
        const activeCaremap = this.careMapState.getActiveCaremap() as WithLinks<ICareMap>;

        if (!activeCaremap) {
            throw new Error('there is no active caremap');
        }

        let model = this.careMapIdToModel[activeCaremap.careMapId!];
        if (!model) {
            model = new InsightsModel(activeCaremap, this);
            this.careMapIdToModel[activeCaremap.careMapId!] = model;
        }

        if (this.activeCareMapId !== activeCaremap.careMapId) {
            this.activeCareMapId = activeCaremap.careMapId!;

            // load() surfaces its own failure inline (sets loadFailed + logs) and no longer rejects, so
            // there's nothing to catch here; the insights page/drawer render an inline retry.
            void model.load();

            // forActiveCaremap is likely called in a render
            // so we need to avoid doing anything sync that might change state
            // or react gets upset
            setTimeout(() => {
                // if the model changes, we change, that way components can just subscribe to the factory
                model.subscribe(() => {
                    this.setState({});
                });
            });
        }

        return this.careMapIdToModel[activeCaremap.careMapId!];
    };
}
